/**
 * Where a brush will actually land, and the one rule both the paint and the
 * preview obey.
 *
 * A brush on a body is a ball, not a disc. On a limb narrower than the brush it
 * reaches around onto that limb's other faces — which are laid out somewhere
 * else entirely in the template atlas, so a single dab shows up as separate
 * patches and reads as paint teleporting. It is not; it is one continuous mark
 * on the body, seen flattened.
 *
 * That surprise is the reason this module exists. The rule deciding which
 * covered texels a stroke may write is shared with the preview that draws them,
 * because a preview that disagrees with the paint is worse than no preview: it
 * teaches a model of the tool that is wrong, and the only way to discover that
 * is to have already ruined a design.
 */

import type { CoveredTexel } from "../../shared/brush";
import type { RegionId } from "../../shared/format";
import { type BodySide, type PartGroup, partGroup, partSide } from "../../shared/parts";
import { type PositionMap, partAt } from "../../shared/position-map";
import { allows, type Selection } from "../../shared/selection";

export interface LandingRules {
  map: PositionMap;
  /** The face under the pointer. Its own texels are drawn by the stroke itself. */
  ownRegion: RegionId | null;
  /** The part group under the pointer, or null when the pointer is off the body. */
  ownGroup: PartGroup | "skin" | null;
  /**
   * Which side of the body the pointer is on, or null for the torso and head.
   *
   * Separate from the group because the group cannot express it: both arms are
   * "arms". See `partSide`.
   *
   * Required rather than optional, and `null` has to be written out. Omitting
   * it skips the side check and silently restores the bug this was added to
   * fix — which is precisely the kind of quiet regression a type can prevent
   * and a comment cannot.
   */
  ownSide: BodySide | null;
  /** False keeps a stroke on one body part — the "Onto other parts" control. */
  crossParts: boolean;
  /** Faces the user has picked. Empty means all of them. */
  selection: Selection;
}

/**
 * Whether this covered texel is one the stroke may write.
 *
 * Excludes the pointer's own face, which the stroke draws for itself at the
 * right shape and softness — this function is only ever asked about the parts
 * that carry across a fold.
 */
export function landsHere(t: CoveredTexel, rules: LandingRules): boolean {
  // Its own face is the stroke's business, not the crossing's.
  if (t.region === rules.ownRegion) return false;

  /*
    A fold is not a way around the selection.

    The stroke's own outline is clipped by the paintable mask; crossings are
    not, because they are written as individual texels. Without this a stroke
    on a chosen face would carry onto one the user excluded, which is precisely
    what excluding it was meant to prevent.
  */
  if (!allows(rules.selection, t.region)) return false;

  /*
    Same limb unless told otherwise.

    Grouped rather than compared part by part, because an R15 arm is three
    parts and an elbow is a fold rather than a joint anybody thinks about.
  */
  if (!rules.crossParts && rules.ownGroup) {
    const p = partAt(rules.map, t.x, t.y);
    if (p && partGroup(p) !== rules.ownGroup) return false;

    /*
      Same group is not the same limb.

      `partGroup` puts both arms in "arms" and both legs in "legs", so the check
      above passed for the *opposite* limb and a wide stroke carried across the
      body — with the one setting that exists to prevent it switched off. It
      reads as a mirror nobody turned on, which is why it took a report rather
      than a glance to find.

      Sides are compared only when the pointer has one: a torso stroke has no
      side, and the arm it reaches is a different group anyway.
    */
    if (p && rules.ownSide && partSide(p) !== rules.ownSide) return false;
  }

  return true;
}
